import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";

const PaymentForm = ({ userDetails, selectedProfile, onPaymentSuccess }) => {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState("card");
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    feeStructure: false,
  });
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
    upiId: "",
  });
  const [errors, setErrors] = useState({});

  const paymentMethods = [
    {
      id: "card",
      name: "Credit/Debit Card",
      icon: "CreditCard",
      description: "Visa, Mastercard, RuPay",
    },
    {
      id: "upi",
      name: "UPI",
      icon: "Smartphone",
      description: "PhonePe, GPay, Paytm",
    },
    {
      id: "netbanking",
      name: "Net Banking",
      icon: "Building",
      description: "All major banks",
    },
    {
      id: "wallet",
      name: "Wallet",
      icon: "Wallet",
      description: "Paytm, Amazon Pay",
    },
  ];

  const razorpayLinks = {
    "it-support": "https://rzp.io/rzp/Akhaqcb",
    "fullstack-developer": "https://rzp.io/l/MIQjpKIY3R",
    "mobile-app-developer": "https://rzp.io/rzp/97jKs4pP",
    devops: "https://rzp.io/rzp/cofhC5E3",
    "data-analytics": "https://rzp.io/rzp/6mo1XxD3",
    "artificial-intelligence": "https://rzp.io/rzp/GrCXHsq",
  };

  const paymentLink =
    razorpayLinks[selectedProfile] || razorpayLinks["it-support"];

  const profileFees = {
    "it-support": 4999,
    "software-developer": 6999,
  };

  const baseFee = profileFees[selectedProfile] || profileFees["it-support"];
  const gst = Math.round(baseFee * 0.18);
  const totalAmount = baseFee + gst;

  const validateForm = () => {
    const newErrors = {};

    // Check agreements
    if (!agreements.terms) newErrors.terms = "Please accept Terms & Conditions";
    if (!agreements.privacy) newErrors.privacy = "Please accept Privacy Policy";
    if (!agreements.feeStructure)
      newErrors.feeStructure = "Please acknowledge Fee Structure";

    // Validate payment method specific fields
    if (selectedMethod === "card") {
      if (!paymentDetails.cardNumber)
        newErrors.cardNumber = "Card number is required";
      if (!paymentDetails.expiryDate)
        newErrors.expiryDate = "Expiry date is required";
      if (!paymentDetails.cvv) newErrors.cvv = "CVV is required";
      if (!paymentDetails.cardholderName)
        newErrors.cardholderName = "Cardholder name is required";
    } else if (selectedMethod === "upi") {
      if (!paymentDetails.upiId) newErrors.upiId = "UPI ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Mock payment success
      const paymentResult = {
        success: true,
        transactionId: `TXN${Date.now()}`,
        registrationId: `REG${Date.now()}`,
        amount: totalAmount,
        method: selectedMethod,
      };

      onPaymentSuccess(paymentResult);
      navigate("/registration-success-confirmation", {
        state: {
          paymentResult,
          userDetails,
          selectedProfile,
        },
      });
    } catch (error) {
      setErrors({ payment: "Payment failed. Please try again." });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPaymentDetails((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleAgreementChange = (field, checked) => {
    setAgreements((prev) => ({ ...prev, [field]: checked }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  // useEffect(() => {
  //   const iframe = iframeRef.current;
  //   if (!iframe) return;

  //   const handleLoad = () => {
  //     try {
  //       const iframeWindow = iframe.contentWindow;
  //       const iframeLocation = iframeWindow?.location?.href;

  //       console.log("Iframe navigated:", iframeLocation);

  //       if (iframeLocation?.includes("/registration-success-confirmation")) {
  //         // internally navigate to your React confirmation page
  //         navigate("/confirmation");
  //       }
  //     } catch (err) {
  //       // Will throw only while iframe is on rzp.io domain (before redirect)
  //       console.log("Cross-origin, waiting for redirect...", err.message);
  //     }
  //     setIframeLoading(false);
  //   };

  //   iframe.addEventListener("load", handleLoad);
  //   return () => iframe.removeEventListener("load", handleLoad);
  // }, [navigate]);

  return (
    <div className="bg-surface border border-border rounded-lg p-6 shadow-subtle">
      <h2 className="text-lg font-semibold text-foreground mb-6 flex items-center">
        <Icon name="Lock" size={20} className="mr-2" />
        Secure Payment
      </h2>

      {/* User Details Summary */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <h3 className="font-medium text-foreground mb-3">Payment Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-text-secondary">Name:</span>
            <span className="text-foreground font-medium">
              {userDetails.fullName}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Email:</span>
            <span className="text-foreground">{userDetails.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Mobile:</span>
            <span className="text-foreground">{userDetails.mobileNumber}</span>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      {/* <div className="mb-6">
        <h3 className="font-medium text-foreground mb-4">Choose Payment Method</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {paymentMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => setSelectedMethod(method.id)}
              className={`
                p-4 border rounded-lg transition-smooth text-left
                ${selectedMethod === method.id
                  ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/50 text-foreground'
                }
              `}
            >
              <div className="flex flex-col items-center text-center">
                <Icon 
                  name={method.icon} 
                  size={24} 
                  className="mb-2"
                  color={selectedMethod === method.id ? 'var(--color-primary)' : 'currentColor'}
                />
                <span className="font-medium text-sm">{method.name}</span>
                <span className="text-xs text-text-secondary mt-1">{method.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Payment Form Fields */}
      {/* <div className="mb-6">
        {selectedMethod === 'card' && (
          <div className="space-y-4">
            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={paymentDetails.cardNumber}
              onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
              error={errors.cardNumber}
              maxLength={19}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                placeholder="MM/YY"
                value={paymentDetails.expiryDate}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 2) {
                    value = value.substring(0, 2) + '/' + value.substring(2, 4);
                  }
                  handleInputChange('expiryDate', value);
                }}
                error={errors.expiryDate}
                maxLength={5}
              />
              <Input
                label="CVV"
                type="password"
                placeholder="123"
                value={paymentDetails.cvv}
                onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, ''))}
                error={errors.cvv}
                maxLength={4}
              />
            </div>
            <Input
              label="Cardholder Name"
              type="text"
              placeholder="Name as on card"
              value={paymentDetails.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e.target.value)}
              error={errors.cardholderName}
            />
          </div>
        )}

        {selectedMethod === 'upi' && (
          <div className="space-y-4">
            <Input
              label="UPI ID"
              type="text"
              placeholder="yourname@paytm"
              value={paymentDetails.upiId}
              onChange={(e) => handleInputChange('upiId', e.target.value)}
              error={errors.upiId}
            />
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center">
                <Icon name="Info" size={16} className="mr-2 text-primary" />
                <span className="text-sm text-foreground">
                  You will be redirected to your UPI app to complete the payment
                </span>
              </div>
            </div>
          </div>
        )}

        {selectedMethod === 'netbanking' && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Building" size={16} className="mr-2 text-primary" />
              <span className="text-sm text-foreground">
                You will be redirected to your bank's secure website
              </span>
            </div>
          </div>
        )}

        {selectedMethod === 'wallet' && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Wallet" size={16} className="mr-2 text-primary" />
              <span className="text-sm text-foreground">
                Choose your preferred wallet on the next screen
              </span>
            </div>
          </div>
        )}
      </div>  */}

      {/* Razorpay Payment Page */}
      <div style={{ width: "100%", height: "600px", position: "relative" }}>
        {agreements.terms && agreements.privacy && agreements.feeStructure ? (
          <>
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                {/* simple Tailwind spinner */}
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={paymentLink}
              title="Razorpay Payment"
              style={{ width: "100%", height: "100%", border: "none" }}
              // allow="payment *; camera *; clipboard-write *; publickey-credentials-get *; publickey-credentials-create *; web-share *; otp-credentials *"
              onLoad={() => {
                const iframe = iframeRef.current;
                try {
                  const iframeWindow = iframe.contentWindow;
                  const iframeLocation = iframeWindow?.location?.href;

                  console.log("Iframe navigated:", iframeLocation);

                  // Will only succeed if redirected to *your* domain
                  if (
                    iframeLocation?.includes(
                      "/registration-success-confirmation"
                    )
                  ) {
                    navigate("/registration-success-confirmation");
                  }
                } catch (err) {
                  // While iframe is on rzp.io → cross-origin, cannot read href
                  console.log(
                    "Still on Razorpay (cross-origin), waiting for redirect..."
                  );
                }
                setIframeLoading(false);
              }}
            />
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-center text-m text-text-secondary border border-dashed rounded-lg p-4">
            <p>Please accept all terms and policies to proceed with payment</p>
          </div>
        )}
      </div>

      {/* Agreements */}
      <div className="space-y-4 mb-6 mt-6">
        <Checkbox
          label="I agree to the Terms & Conditions"
          checked={agreements.terms}
          onChange={(e) => handleAgreementChange("terms", e.target.checked)}
          error={errors.terms}
          required
        />
        <Checkbox
          label="I accept the Privacy Policy"
          checked={agreements.privacy}
          onChange={(e) => handleAgreementChange("privacy", e.target.checked)}
          error={errors.privacy}
          required
        />
        <Checkbox
          label="I acknowledge the Fee Structure and Refund Policy"
          checked={agreements.feeStructure}
          onChange={(e) =>
            handleAgreementChange("feeStructure", e.target.checked)
          }
          error={errors.feeStructure}
          required
        />
      </div>

      {/* Security Notice */}
      {/* <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <Icon
            name="Shield"
            size={16}
            className="mr-2 text-success mt-0.5 flex-shrink-0"
          />
          <div>
            <p className="text-sm font-medium text-success">Secure Payment</p>
            <p className="text-xs text-success/80 mt-1">
              Your payment information is encrypted with 256-bit SSL security
            </p>
          </div>
        </div>
      </div> */}

      {/* Payment Error */}
      {/* {errors.payment && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-2 text-error" />
            <span className="text-sm text-error">{errors.payment}</span>
          </div>
        </div>
      )} */}

      {/* Payment Button */}
      {/* <Button
        variant="default"
        size="lg"
        fullWidth
        loading={isProcessing}
        onClick={handlePayment}
        disabled={!agreements.terms || !agreements.privacy || !agreements.feeStructure}
        iconName="Lock"
        iconPosition="left"
      >
        {isProcessing ? 'Processing Payment...' : `Pay ₹${totalAmount.toLocaleString('en-IN')}`}
      </Button> */}

      <p className="text-xs text-text-secondary text-center mt-4">
        By clicking "Pay", you authorize Solvarsity to charge your payment 
        method
      </p>
    </div>
  );
};

export default PaymentForm;
